import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  Chip,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  SwapHoriz as SwapHorizIcon,
  Phone as PhoneIcon,
  Inbox as InboxIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { formatDateTimeBR } from '../utils/dateUtils';

const SITUACAO_OPTIONS = ['Em análise', 'Resolvido'] as const;

const SITUACAO_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  'Em análise': { bg: '#fef3c7', color: '#b45309', border: '#f59e0b' },
  'Resolvido': { bg: '#dcfce7', color: '#15803d', border: '#22c55e' },
};

// Lados da troca — cores distintas para leitura imediata de "quem virou quem"
const SIDE_A = { bg: '#fffbeb', border: '#fde68a', label: '#b45309' };
const SIDE_B = { bg: '#eff6ff', border: '#bfdbfe', label: '#1d4ed8' };

interface Swap {
  id: string;
  responsible: string;
  orderNumber: string;
  swappedOrderNumber: string;
  clientPhone1?: string | null;
  clientPhone2?: string | null;
  situacao: string;
  observation?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  responsible: string;
  orderNumber: string;
  swappedOrderNumber: string;
  clientPhone1: string;
  clientPhone2: string;
  situacao: string;
  observation: string;
}

const emptyForm: FormData = {
  responsible: '',
  orderNumber: '',
  swappedOrderNumber: '',
  clientPhone1: '',
  clientPhone2: '',
  situacao: 'Em análise',
  observation: '',
};

const initials = (name: string) =>
  name.trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2) || '?';

const PedidosTrocados: React.FC = () => {
  const [rows, setRows] = useState<Swap[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [situacaoFilter, setSituacaoFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  const [formDialog, setFormDialog] = useState<{ open: boolean; mode: 'add' | 'edit'; id?: string }>({
    open: false,
    mode: 'add',
  });
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [situacaoDialog, setSituacaoDialog] = useState<{ open: boolean; id?: string; current?: string }>({
    open: false,
  });

  const [viewDialog, setViewDialog] = useState<{ open: boolean; record?: Swap }>({ open: false });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') =>
    setSnackbar({ open: true, message, severity });

  // A situação é filtrada em memória para os cards de resumo refletirem sempre o período inteiro
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllOrderSwaps(startDate || undefined, endDate || undefined);
      setRows(data || []);
    } catch (error) {
      console.error('Erro ao carregar pedidos trocados:', error);
      showSnackbar('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = rows.filter((r) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      r.orderNumber.toLowerCase().includes(term) ||
      r.swappedOrderNumber.toLowerCase().includes(term) ||
      r.responsible.toLowerCase().includes(term) ||
      (r.clientPhone1 || '').toLowerCase().includes(term) ||
      (r.clientPhone2 || '').toLowerCase().includes(term) ||
      (r.observation || '').toLowerCase().includes(term);
    const matchesSituacao = !situacaoFilter || r.situacao === situacaoFilter;
    return matchesSearch && matchesSituacao;
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Resumo do período (independente do filtro de situação)
  const totalPeriodo = rows.length;
  const emAnalise = rows.filter((r) => r.situacao === 'Em análise').length;
  const resolvidos = rows.filter((r) => r.situacao === 'Resolvido').length;

  const toggleSituacaoFilter = (s: string) => {
    setSituacaoFilter((prev) => (prev === s ? '' : s));
    setPage(0);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelected(new Set(filtered.map((r) => r.id)));
    else setSelected(new Set());
  };
  const handleSelectOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const openAdd = () => {
    setForm(emptyForm);
    setFormDialog({ open: true, mode: 'add' });
  };

  const openEdit = (r: Swap) => {
    setForm({
      responsible: r.responsible,
      orderNumber: r.orderNumber,
      swappedOrderNumber: r.swappedOrderNumber,
      clientPhone1: r.clientPhone1 || '',
      clientPhone2: r.clientPhone2 || '',
      situacao: r.situacao,
      observation: r.observation || '',
    });
    setFormDialog({ open: true, mode: 'edit', id: r.id });
  };

  const handleSaveForm = async () => {
    if (!form.responsible.trim() || !form.orderNumber.trim() || !form.swappedOrderNumber.trim()) {
      showSnackbar('Preencha o responsável e os dois números de pedido', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        responsible: form.responsible.trim(),
        orderNumber: form.orderNumber.trim(),
        swappedOrderNumber: form.swappedOrderNumber.trim(),
        clientPhone1: form.clientPhone1.trim() || null,
        clientPhone2: form.clientPhone2.trim() || null,
        situacao: form.situacao,
        observation: form.observation || null,
      };
      if (formDialog.mode === 'add') {
        await apiService.createOrderSwap(payload);
        showSnackbar('Pedido trocado cadastrado com sucesso!', 'success');
      } else if (formDialog.id) {
        await apiService.updateOrderSwap(formDialog.id, payload);
        showSnackbar('Pedido trocado atualizado com sucesso!', 'success');
      }
      setFormDialog({ open: false, mode: 'add' });
      loadData();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.error || 'Erro ao salvar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeSituacao = async (novaSituacao: string) => {
    if (!situacaoDialog.id) return;
    try {
      await apiService.updateOrderSwap(situacaoDialog.id, { situacao: novaSituacao });
      setSituacaoDialog({ open: false });
      showSnackbar('Situação atualizada!', 'success');
      loadData();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.error || 'Erro ao atualizar situação', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este registro?')) return;
    try {
      await apiService.deleteOrderSwap(id);
      showSnackbar('Registro excluído', 'success');
      loadData();
    } catch {
      showSnackbar('Erro ao excluir', 'error');
    }
  };

  const handleDeleteMultiple = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Excluir ${selected.size} registro(s)?`)) return;
    try {
      await apiService.deleteMultipleOrderSwaps(Array.from(selected));
      showSnackbar(`${selected.size} registro(s) excluído(s)`, 'success');
      setSelected(new Set());
      loadData();
    } catch {
      showSnackbar('Erro ao excluir', 'error');
    }
  };

  const situacaoChip = (situacao: string, onClick?: () => void) => {
    const c = SITUACAO_COLORS[situacao] || { bg: '#e5e7eb', color: '#374151', border: '#9ca3af' };
    return (
      <Chip
        icon={situacao === 'Resolvido'
          ? <CheckCircleIcon sx={{ fontSize: 16, color: `${c.color} !important` }} />
          : <HourglassIcon sx={{ fontSize: 16, color: `${c.color} !important` }} />}
        label={situacao}
        size="small"
        onClick={onClick}
        sx={{
          backgroundColor: c.bg,
          color: c.color,
          fontWeight: 700,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { filter: 'brightness(0.95)' } : undefined,
        }}
      />
    );
  };

  // Bloco de um lado da troca: pedido + contato do cliente, pareados
  const sideBlock = (
    label: string,
    orderNumber: string,
    phone: string | null | undefined,
    tone: typeof SIDE_A,
  ) => (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        backgroundColor: tone.bg,
        border: `1px solid ${tone.border}`,
        minWidth: 150,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: tone.label, letterSpacing: 0.4, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
        #{orderNumber}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <PhoneIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
        <Typography variant="caption" sx={{ color: phone ? '#4b5563' : '#c0c4cc' }}>
          {phone || 'sem contato'}
        </Typography>
      </Box>
    </Box>
  );

  const statCard = (
    label: string,
    value: number,
    icon: React.ReactNode,
    tint: string,
    borderColor: string,
    active: boolean,
    onClick?: () => void,
  ) => (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: tint,
        border: active ? `2px solid ${borderColor}` : '1px solid rgba(0,0,0,0.06)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' } : undefined,
      }}
    >
      {icon}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1, color: '#1a1a1a' }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      </Box>
    </Paper>
  );

  const colSpan = 7;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon sx={{ color: '#D4AF37', fontSize: 36 }} /> Pedidos Trocados
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Controle de pedidos entregues trocados entre clientes — quem foi o responsável, quais pedidos se cruzaram e o contato de cada cliente.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600, px: 3, py: 1.5, '&:hover': { backgroundColor: '#B8941F' } }}
        >
          Nova Troca
        </Button>
      </Box>

      {/* Resumo do período — clicável para filtrar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          {statCard('Trocas no período', totalPeriodo, <InboxIcon sx={{ fontSize: 34, color: '#6b7280' }} />, '#f9fafb', '#9ca3af', !situacaoFilter, () => { setSituacaoFilter(''); setPage(0); })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {statCard('Em análise', emAnalise, <HourglassIcon sx={{ fontSize: 34, color: '#f59e0b' }} />, '#fffbeb', '#f59e0b', situacaoFilter === 'Em análise', () => toggleSituacaoFilter('Em análise'))}
        </Grid>
        <Grid item xs={12} sm={4}>
          {statCard('Resolvidos', resolvidos, <CheckCircleIcon sx={{ fontSize: 34, color: '#22c55e' }} />, '#f0fdf4', '#22c55e', situacaoFilter === 'Resolvido', () => toggleSituacaoFilter('Resolvido'))}
        </Grid>
      </Grid>

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por pedido, responsável ou telefone"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth size="small" label="Data inicial" type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth size="small" label="Data final" type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                sx={{ borderColor: '#D4AF37', color: '#B8941F', height: 40, '&:hover': { borderColor: '#B8941F', backgroundColor: 'rgba(212,175,55,0.04)' } }}
              >
                Atualizar
              </Button>
              {selected.size > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteMultiple}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  {selected.size}
                </Button>
              )}
            </Grid>
          </Grid>
          {situacaoFilter && (
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label={`Filtrando: ${situacaoFilter}`}
                onDelete={() => setSituacaoFilter('')}
                size="small"
                sx={{ backgroundColor: SITUACAO_COLORS[situacaoFilter]?.bg, color: SITUACAO_COLORS[situacaoFilter]?.color, fontWeight: 600 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                <TableCell padding="checkbox" sx={{ px: 1 }}>
                  <Checkbox
                    checked={selected.size === filtered.length && filtered.length > 0}
                    indeterminate={selected.size > 0 && selected.size < filtered.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>A TROCA</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>RESPONSÁVEL</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>SITUAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>OBSERVAÇÃO</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F' }}>REGISTRADO EM</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#B8941F', textAlign: 'center' }}>AÇÕES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                    <SwapHorizIcon sx={{ fontSize: 48, color: '#e5e7eb', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      {rows.length === 0
                        ? 'Nenhuma troca registrada no período. Clique em "Nova Troca" para começar.'
                        : 'Nenhum resultado para os filtros aplicados.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((r) => {
                  const stripe = SITUACAO_COLORS[r.situacao]?.border || '#9ca3af';
                  return (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{ '& > td:first-of-type': { borderLeft: `4px solid ${stripe}` } }}
                    >
                      <TableCell padding="checkbox" sx={{ px: 1 }}>
                        <Checkbox checked={selected.has(r.id)} onChange={() => handleSelectOne(r.id)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {sideBlock('PEDIDO', r.orderNumber, r.clientPhone1, SIDE_A)}
                          <SwapHorizIcon sx={{ color: '#D4AF37', fontSize: 22, flexShrink: 0 }} />
                          {sideBlock('TROCADO POR', r.swappedOrderNumber, r.clientPhone2, SIDE_B)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, backgroundColor: '#e5e7eb', color: '#4b5563' }}>
                            {initials(r.responsible)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.responsible}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {situacaoChip(r.situacao, () => setSituacaoDialog({ open: true, id: r.id, current: r.situacao }))}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={r.observation || ''}>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: r.observation ? 'inherit' : '#c0c4cc' }}>
                            {r.observation || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                        {formatDateTimeBR(r.createdAt)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <Tooltip title="Visualizar">
                          <IconButton size="small" onClick={() => setViewDialog({ open: true, record: r })} sx={{ color: '#D4AF37' }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#B8941F' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => handleDelete(r.id)} sx={{ color: '#ef4444' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>

      {/* Dialog: Criar / Editar */}
      <Dialog open={formDialog.open} onClose={() => setFormDialog({ open: false, mode: 'add' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon /> {formDialog.mode === 'add' ? 'Nova Troca de Pedidos' : 'Editar Troca'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Responsável pela troca" required
                value={form.responsible}
                onChange={(e) => setForm({ ...form, responsible: e.target.value })}
                placeholder="Nome da pessoa ou setor (ex.: Expedição)"
              />
            </Grid>

            {/* Lado A */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: SIDE_A.bg, border: `1px solid ${SIDE_A.border}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: SIDE_A.label, display: 'block', mb: 1 }}>
                  PEDIDO
                </Typography>
                <TextField
                  fullWidth size="small" label="Nº do pedido" required sx={{ mb: 1.5, backgroundColor: '#fff' }}
                  value={form.orderNumber}
                  onChange={(e) => setForm({ ...form, orderNumber: e.target.value })}
                />
                <TextField
                  fullWidth size="small" label="Contato do cliente" sx={{ backgroundColor: '#fff' }}
                  value={form.clientPhone1}
                  onChange={(e) => setForm({ ...form, clientPhone1: e.target.value })}
                />
              </Box>
            </Grid>

            {/* Lado B */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: SIDE_B.bg, border: `1px solid ${SIDE_B.border}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: SIDE_B.label, display: 'block', mb: 1 }}>
                  TROCADO POR
                </Typography>
                <TextField
                  fullWidth size="small" label="Nº do outro pedido" required sx={{ mb: 1.5, backgroundColor: '#fff' }}
                  value={form.swappedOrderNumber}
                  onChange={(e) => setForm({ ...form, swappedOrderNumber: e.target.value })}
                />
                <TextField
                  fullWidth size="small" label="Contato do cliente" sx={{ backgroundColor: '#fff' }}
                  value={form.clientPhone2}
                  onChange={(e) => setForm({ ...form, clientPhone2: e.target.value })}
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Situação</InputLabel>
                <Select value={form.situacao} label="Situação" onChange={(e) => setForm({ ...form, situacao: e.target.value })}>
                  {SITUACAO_OPTIONS.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Observação" multiline rows={3}
                value={form.observation}
                onChange={(e) => setForm({ ...form, observation: e.target.value })}
                placeholder="O que aconteceu, o que já foi feito..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormDialog({ open: false, mode: 'add' })} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleSaveForm}
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#D4AF37', color: '#000', '&:hover': { backgroundColor: '#B8941F' } }}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Situação */}
      <Dialog open={situacaoDialog.open} onClose={() => setSituacaoDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>Alterar Situação</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {SITUACAO_OPTIONS.map((s) => {
              const c = SITUACAO_COLORS[s];
              const isCurrent = s === situacaoDialog.current;
              return (
                <Button
                  key={s}
                  startIcon={s === 'Resolvido' ? <CheckCircleIcon /> : <HourglassIcon />}
                  onClick={() => handleChangeSituacao(s)}
                  sx={{
                    justifyContent: 'flex-start',
                    backgroundColor: c.bg,
                    color: c.color,
                    fontWeight: 700,
                    py: 1.2,
                    border: isCurrent ? `2px solid ${c.color}` : '2px solid transparent',
                    '&:hover': { backgroundColor: c.bg, filter: 'brightness(0.95)' },
                  }}
                >
                  {s}{isCurrent ? '  (atual)' : ''}
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSituacaoDialog({ open: false })} sx={{ color: '#666' }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Visualizar */}
      <Dialog open={viewDialog.open} onClose={() => setViewDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1a1a', color: '#D4AF37', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SwapHorizIcon /> Detalhes da Troca</Box>
          <IconButton onClick={() => setViewDialog({ open: false })} sx={{ color: '#D4AF37' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewDialog.record && (
            <Box sx={{ mt: 1 }}>
              {/* A troca em destaque */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                {sideBlock('PEDIDO', viewDialog.record.orderNumber, viewDialog.record.clientPhone1, SIDE_A)}
                <SwapHorizIcon sx={{ color: '#D4AF37', fontSize: 28 }} />
                {sideBlock('TROCADO POR', viewDialog.record.swappedOrderNumber, viewDialog.record.clientPhone2, SIDE_B)}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Responsável</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewDialog.record.responsible}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Situação</Typography>
                  <Box sx={{ mt: 0.5 }}>{situacaoChip(viewDialog.record.situacao)}</Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Registrado em</Typography>
                  <Typography variant="body2">{formatDateTimeBR(viewDialog.record.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Observação</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewDialog.record.observation || '—'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PedidosTrocados;
